import * as React from "react";
import {
    Card,
    CardContent,
    CardHeader,
    Grid,
    TextField,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    TableContainer,
    TableCell,
    Table,
    TableHead,
    TableRow,
    TableBody,
    LinearProgress,
    Divider,
    Stack,
    Alert,
    AlertTitle,
    Skeleton,
    CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorIcon from "@mui/icons-material/Error";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

interface EnvVariable {
    key: string;
    value: string;
}

interface PreProdDeployProps {
    activeEnv: string;
    onTriggerActiveServer: (server: string) => void;
    socket?: any;
}

interface DeploymentStatus {
    status: string;
    taskDefinition: string;
    desiredCount: number;
    pendingCount: number;
    runningCount: number;
    failedTasks: number;
    lastUpdated: Date;
    runningTask: string;
    version: string;
    clientCount: number;
}

interface ServerStats {
    version: string;
    pids: {
        pid?: number;
        connection: number;
    }[];
}

interface PreProdDeployState {
    currentServer: string | null;
    targetServer: string | null;
    domains: string[];
    latestImage: {
        imageDigest: string;
        imageTag: string;
        buildNumber: number;
    };
    isConfirmDialogOpen: boolean;
    isLoading: boolean;
    envVariables: EnvVariable[];
    blue: {
        serviceName: string;
        deploymentStatus: DeploymentStatus | null;
        domain: string;
    };
    green: {
        serviceName: string;
        deploymentStatus: DeploymentStatus | null;
        domain: string;
    };
    statusCheckInterval: NodeJS.Timeout | null;
    isGetDeployInfo: boolean;
    isDeploying: boolean;
    statusCheckProgress: number;
    activeServer: string | null;    
    serverStats: Record<string, { totalConnections: number; pids: { pid?: number; connection: number, hostname?: string }[] }>;
    isConnectionDetailsOpen: boolean;
    selectedVersion: string | null;
}

export class PreProdDeploy extends React.Component<PreProdDeployProps, PreProdDeployState> {
    private progressInterval: NodeJS.Timeout | null = null;
    private isMountedFlag: boolean = false;
    constructor(props) {
        super(props);       
        this.state = {
            currentServer: null,
            targetServer: null,
            domains: [],
            latestImage: {
                imageDigest: "",
                imageTag: "",
                buildNumber: 0
            },
            isConfirmDialogOpen: false,
            isLoading: true,
            envVariables: [],
            blue: {
                serviceName: "",
                deploymentStatus: null,
                domain: ""
            },
            green: {
                serviceName: "",
                deploymentStatus: null,
                domain: ""
            },
            statusCheckInterval: null,
            isGetDeployInfo: false,
            isDeploying: false,
            statusCheckProgress: 0,
            activeServer: null,
            serverStats: {},
            isConnectionDetailsOpen: false,
            selectedVersion: null,
        };
    }

    componentDidMount() {
        this.isMountedFlag = true;
        // Register socket listener
        if (this.props.socket) {
            const socketId = this.props.socket.id;
            // Register socket for deployment updates
            axios.post(`${Config.getInstance.apiUrl}/api/deploy/register`, {
                socketId
            }, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                }
            });

            this.props.socket.on("ServerStatsUpdate", (data: ServerStats[]) => {
                console.log("ServerStatsUpdate", data);
                // Calculate total connections for each version
                const versionStats = data.reduce((acc, item) => {
                    const totalConnections = item.pids.reduce((sum, pid) => sum + pid.connection, 0);
                    acc[item.version] = {
                        totalConnections,
                        pids: item.pids
                    };
                    return acc;
                }, {} as Record<string, { totalConnections: number; pids: { pid?: number; connection: number, hostname?: string }[] }>);

                console.log("versionStats", versionStats);
                // Store the stats in state
                this.setState({ serverStats: versionStats });

                // Update blue service stats
                if (this.state.blue?.deploymentStatus?.version) {
                    const blueStats = versionStats[this.state.blue.deploymentStatus.version];
                    if (blueStats) {
                        const b = this.state.blue;
                        if (b?.deploymentStatus) {
                            b.deploymentStatus.clientCount = blueStats.totalConnections;
                            this.setState({ blue: b });
                        }
                    }
                }

                // Update green service stats
                if (this.state.green?.deploymentStatus?.version) {
                    const greenStats = versionStats[this.state.green.deploymentStatus.version];
                    if (greenStats) {
                        const g = this.state.green;
                        if (g?.deploymentStatus) {
                            g.deploymentStatus.clientCount = greenStats.totalConnections;
                            this.setState({ green: g });
                        }
                    }
                }
            });
            this.props.socket.on("DeploymentStatusUpdate", (data) => {
                console.log("DeploymentStatusUpdate", data);
                if(data.status === 'COMPLETED'){
                    this.fetchDeployInfo();
                }
            });
        }
        this.fetchDeployInfo();
    }
    requestServerStats = () => {
        if (this.props.socket) {
            console.log("Requesting server stats");
            this.props.socket.emit("ServerStatsUpdate");
        }
    }
    componentWillUnmount() {
        this.isMountedFlag = false;
        this.stopStatusCheck();
        // Unregister socket listener
        if (this.props.socket) {
            const socketId = this.props.socket.id;
            axios.post(`${Config.getInstance.apiUrl}/api/deploy/unregister`, {
                socketId
            }, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                }
            });
            this.props.socket.off("DeploymentStatusUpdate");
        }
    }

    startStatusCheck = () => {
        //temp disable status check
        return;
        console.log("Starting status check");
        // Clear any existing interval
        this.stopStatusCheck();

        const CHECK_INTERVAL = 60000; // 60 seconds
        const PROGRESS_INTERVAL = 1000; // Update progress every second
        let progress = 0;

        // Start progress update interval
        const progressInterval = setInterval(() => {
            progress = (progress + (100 * PROGRESS_INTERVAL / CHECK_INTERVAL)) % 100;
            this.setState({ statusCheckProgress: progress });
        }, PROGRESS_INTERVAL);

        // Start new interval
        const interval = setInterval(async () => {
            try {
                const { blue, green } = this.state;

                // Check which services are in progress
                const isBlueInProgress = blue.deploymentStatus?.status === 'IN_PROGRESS';
                const isGreenInProgress = green.deploymentStatus?.status === 'IN_PROGRESS';

                if (isBlueInProgress || isGreenInProgress) {
                    type StatusResponse = {
                        type: 'blue' | 'green';
                        status: NonNullable<typeof blue.deploymentStatus>;
                    };

                    const requests: Promise<StatusResponse>[] = [];

                    // Only fetch status for services that are in progress
                    if (isBlueInProgress) {
                        requests.push(
                            axios.get(`${Config.getInstance.apiUrl}/api/server-config/deploy-status`, {
                                headers: {
                                    Authorization: Helpers.getSessionId(),
                                    env: this.props.activeEnv,
                                },
                                params: { serviceName: blue.serviceName }
                            }).then(response => ({
                                type: 'blue' as const,
                                status: response.data.data
                            }))
                        );
                    }

                    if (isGreenInProgress) {
                        requests.push(
                            axios.get(`${Config.getInstance.apiUrl}/api/server-config/deploy-status`, {
                                headers: {
                                    Authorization: Helpers.getSessionId(),
                                    env: this.props.activeEnv,
                                },
                                params: { serviceName: green.serviceName }
                            }).then(response => ({
                                type: 'green' as const,
                                status: response.data.data
                            }))
                        );
                    }

                    // Wait for all in-progress service statuses
                    const results = await Promise.all(requests);

                    // Update state with new statuses
                    const newState = {
                        blue: { ...this.state.blue },
                        green: { ...this.state.green }
                    };

                    results.forEach(result => {
                        if (result.type === 'blue') {
                            newState.blue.deploymentStatus = result.status;
                        } else {
                            newState.green.deploymentStatus = result.status;
                        }
                    });

                    this.setState(newState);

                    // If no services are in progress anymore, stop checking
                    if (newState.blue.deploymentStatus?.status !== 'IN_PROGRESS' &&
                        newState.green.deploymentStatus?.status !== 'IN_PROGRESS') {
                        this.stopStatusCheck();
                        // Refresh deployment info to get final state
                        console.log("Stopping status check");
                        this.fetchDeployInfo();
                    }
                } else {
                    // If no services are in progress, stop checking
                    this.stopStatusCheck();
                }
            } catch (error) {
                console.error('Error checking deployment status:', error);
                this.stopStatusCheck();
            }
        }, CHECK_INTERVAL);

        this.setState({ 
            statusCheckInterval: interval,
            statusCheckProgress: 0 
        });

        // Store progress interval in component for cleanup
        this.progressInterval = progressInterval;
    };

    stopStatusCheck = () => {
        if (this.state.statusCheckInterval) {
            clearInterval(this.state.statusCheckInterval);
            this.setState({ 
                statusCheckInterval: null,
                statusCheckProgress: 0 
            });
        }
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = null;
        }
    };

    fetchDeployInfo = async () => {
        try {
            if (!this.isMountedFlag) {
                return;
            }
            console.log("Fetching deploy info");
            this.setState({ isGetDeployInfo: true });
            const response = await axios.get(`${Config.getInstance.apiUrl}/api/deploy`, {
                headers: {
                    Authorization: Helpers.getSessionId(),
                    env: this.props.activeEnv,
                }
            });

            const { currentServer, domains, lastestImage, blue, green } = response.data;

            // Set the other server as default target
            let defaultTarget = "";
            if (currentServer && domains.length > 0) {
                defaultTarget = domains.find(domain => domain !== currentServer) || domains[0];
            }

            // Set default environment variable if latest image has a build tag
            //const defaultEnvVars = lastestImage?.imageTag ? [{ key: 'VERSION', value: lastestImage.imageTag }] : [];

            this.setState({
                currentServer,
                targetServer: defaultTarget,
                domains,
                latestImage: lastestImage || {
                    imageDigest: "",
                    imageTag: "",
                    buildNumber: 0
                },
                blue,
                green,
                isLoading: false,
                isGetDeployInfo: false,
                envVariables: []
            }, () => {
                // Start status check if any service is in progress
                if (blue.deploymentStatus?.status === 'IN_PROGRESS' ||
                    green.deploymentStatus?.status === 'IN_PROGRESS') {
                    this.startStatusCheck();
                }
            });
        } catch (error: any) {
            PopupMessage.error(`Error fetching deployment info: ${error.message}`);
            this.setState({ isLoading: false });
        }
    };

    handleTargetServerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ targetServer: event.target.value });
    };

    handleDeployClick = () => {
        this.setState({ isConfirmDialogOpen: true });
    };
    handleActiveClick = (server: any) => {
        // set server config game server to active domain
        console.log("Setting active server", server.domain);
        this.setState({ activeServer: server.domain });
    };
    handleAddEnvironmentVariable = () => {
        this.setState(prevState => ({
            envVariables: [...prevState.envVariables, { key: "", value: "" }]
        }));
    };

    handleRemoveEnvironmentVariable = (index: number) => {
        this.setState(prevState => ({
            envVariables: prevState.envVariables.filter((_, i) => i !== index)
        }));
    };

    handleEnvironmentVariableChange = (index: number, field: 'key' | 'value', value: string) => {
        this.setState(prevState => ({
            envVariables: prevState.envVariables.map((env, i) => {
                if (i === index) {
                    return { ...env, [field]: value };
                }
                return env;
            })
        }));
    };

    handleConfirmDeploy = async () => {
        try {
            this.setState({ isDeploying: true });
            const { targetServer, latestImage, envVariables } = this.state;

            const response = await axios.post(
                `${Config.getInstance.apiUrl}/api/deploy`,
                {
                    targetService: targetServer,
                    buildTag: latestImage?.imageTag,
                    environment: envVariables.reduce((acc, { key, value }) => {
                        if (key && value) {
                            acc.push({ name: key, value });
                        }
                        return acc;
                    }, [] as { name: string; value: string }[])
                },
                {
                    headers: {
                        Authorization: Helpers.getSessionId(),
                        env: this.props.activeEnv,
                    }
                }
            );

            if (response.data?.status === "success") {
                PopupMessage.success("Deployment initiated successfully");
                // Start status check immediately after successful deployment
                this.startStatusCheck();
                // Also fetch initial deployment info
                this.fetchDeployInfo();
            } else {
                PopupMessage.error(response.data.message || "Deployment failed");
            }
        } catch (error: any) {
            PopupMessage.error(`Error during deployment: ${error.message}`);
        } finally {
            this.setState({ 
                isConfirmDialogOpen: false,
                isDeploying: false 
            });
        }
    };

    handleCancelDeploy = () => {
        this.setState({ isConfirmDialogOpen: false });
    };

    handleConfirmActiveServer = () => {
        const { activeServer } = this.state;
        if (activeServer) {
            console.log("Setting active server", activeServer);
            this.props.onTriggerActiveServer(activeServer);
        }
        // set server config game server to active domain
        this.setState({ activeServer: null });
    };

    handleOpenConnectionDetails = (version: string) => {
        this.setState({
            isConnectionDetailsOpen: true,
            selectedVersion: version
        });
    };

    handleCloseConnectionDetails = () => {
        this.setState({
            isConnectionDetailsOpen: false,
            selectedVersion: null
        });
    };

    renderConnectionDetailsDialog = () => {
        const { isConnectionDetailsOpen, selectedVersion, serverStats } = this.state;
        if (!selectedVersion || !serverStats[selectedVersion]) return null;

        const pids = serverStats[selectedVersion].pids || [];
        const groupedPids = new Map();
        pids.forEach(pid => {
            const hostname = pid.hostname || 'N/A';
            if (!groupedPids.has(hostname)) {
                groupedPids.set(hostname, []);
            }
            groupedPids.get(hostname).push(pid);
        });

        return (
            <Dialog
                open={isConnectionDetailsOpen}
                onClose={this.handleCloseConnectionDetails}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Connection Details - Version {selectedVersion}
                    <IconButton
                        aria-label="close"
                        onClick={this.handleCloseConnectionDetails}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Hostname</TableCell>
                                    <TableCell>PID</TableCell>
                                    <TableCell>Connections</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.from(groupedPids.entries()).map(([hostname, pidsForHost]) => 
                                    pidsForHost.map((pidObject, index) => (
                                        <TableRow key={`${hostname}-${pidObject.pid || index}`}>
                                            <TableCell>{index === 0 ? hostname : ''}</TableCell>
                                            <TableCell>{pidObject.pid || 'N/A'}</TableCell>
                                            <TableCell>{pidObject.connection}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
            </Dialog>
        );
    };

    renderServiceStatus = (service: { domain: string }, isActive: boolean, isProcessing: boolean) => {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                <Typography variant="h6" color="text.primary">
                    {service.domain === this.state.blue.domain ? 'Blue Service' : 'Green Service'} 
                    {isActive ? <Chip label="Active" color="success" size="small" variant="outlined" sx={{ml: 1}} /> : ''}
                </Typography>
                {!isActive ? (
                    <Button 
                        disabled={isProcessing}
                        variant="contained" 
                        color="primary" 
                        size="small" 
                        onClick={() => this.handleActiveClick(service)}
                    >
                        Active
                    </Button>
                ):''}
            </Box>
        );
    };

    renderDeploymentStatus = (status: string) => {
        const statusColor = status === 'IN_PROGRESS' ? 'info' : status === 'FAILED' ? 'error' : 'success';
        return (
            <Chip label={status} color={statusColor} size="small" variant="outlined" />
        );
    };

    renderDeploymentStatusTable = (deploymentStatus: DeploymentStatus) => {
        return (
            <TableContainer sx={{ border: '1px solid rgba(211,227,253,0.08)', borderRadius: '12px' }}>
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell>{this.renderDeploymentStatus(deploymentStatus.status)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Version</TableCell>
                            <TableCell>{deploymentStatus.version}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Running Tasks</TableCell>
                            <TableCell>{deploymentStatus.runningCount} / {deploymentStatus.desiredCount}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Pending Tasks</TableCell>
                            <TableCell>{deploymentStatus.pendingCount}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Target Task</TableCell>
                            <TableCell>{deploymentStatus.taskDefinition}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Running Task</TableCell>
                            <TableCell>{deploymentStatus.runningTask}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Failed Tasks</TableCell>
                            <TableCell>
                                {deploymentStatus.failedTasks} 
                                {deploymentStatus.failedTasks > 0 && (
                                    <Chip
                                        icon={<ErrorIcon />}
                                        label="Error"
                                        color="error"
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Last Updated</TableCell>
                            <TableCell>{new Date(deploymentStatus.lastUpdated).toLocaleString()}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Total Connections</TableCell>
                            <TableCell>
                                {deploymentStatus.clientCount} 
                                <IconButton onClick={() => this.requestServerStats()}><RefreshIcon /></IconButton>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => this.handleOpenConnectionDetails(deploymentStatus.version)}
                                    sx={{ ml: 1 }}
                                >
                                    View Details
                                </Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
                {this.renderConnectionDetailsDialog()}
            </TableContainer>
        );
    };

    render() {
        const {
            currentServer,
            targetServer,
            latestImage,
            isConfirmDialogOpen,
            isLoading,
            envVariables,
            domains,
            blue,
            green,
            isGetDeployInfo,
            isDeploying,
            statusCheckInterval,
            statusCheckProgress
        } = this.state;

        return (
            <Card elevation={4}>
                <CardHeader
                    title="Deploy Pre-Prod"
                    action={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {statusCheckInterval && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                                    <Chip
                                        icon={<RefreshIcon />}
                                        label="Checking Status"
                                        color="info"
                                        size="small"
                                        variant="outlined"
                                    />
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={statusCheckProgress} 
                                        sx={{ flex: 1 }}
                                    />
                                </Box>
                            )}
                            <IconButton
                                onClick={this.fetchDeployInfo}
                                disabled={isLoading}
                                color="primary"
                                title="Refresh deployment info"
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Box>
                    }
                >
                </CardHeader>
                <CardContent sx={{ p: 0 }}>

                    <Divider />
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Latest Game Version
                        </Typography>
                        {isGetDeployInfo ? (
                            <TableContainer sx={{ border: '1px solid rgba(211,227,253,0.08)', borderRadius: '12px' }}>
                                <Table>
                                    <TableBody>
                                        {[1, 2, 3].map((index) => (
                                            <TableRow key={index}>
                                                <TableCell><Skeleton variant="text" width={100} /></TableCell>
                                                <TableCell><Skeleton variant="text" width={200} /></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <TableContainer sx={{ border: '1px solid rgba(211,227,253,0.08)', borderRadius: '12px' }}>
                                <Table>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Build Number</TableCell>
                                            <TableCell><Typography component="h2">{latestImage?.buildNumber || 'N/A'}</Typography></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Image Tag</TableCell>
                                            <TableCell>{latestImage?.imageTag || 'N/A'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Image Digest</TableCell>
                                            <TableCell>{latestImage?.imageDigest || 'N/A'}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Service Status
                        </Typography>
                        <Grid container spacing={2}>
                            {/* Blue Service Status */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{
                                    borderColor: blue.domain === currentServer ? 'success.main' : 'divider'
                                }} elevation={4}>
                                    <CardContent>
                                        {isGetDeployInfo ? (
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Skeleton variant="circular" width={12} height={12} />
                                                    <Skeleton variant="text" width={100} height={32} />
                                                </Box>
                                                <Skeleton variant="text" width={200} sx={{ mt: 1.5, mb: 2 }} />
                                                <TableContainer sx={{ border: '1px solid rgba(211,227,253,0.08)', borderRadius: '12px' }}>
                                                    <Table>
                                                        <TableBody>
                                                            {[1, 2, 3, 4, 5, 6, 7].map((index) => (
                                                                <TableRow key={index}>
                                                                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                                                                    <TableCell><Skeleton variant="text" width={200} /></TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '50%',
                                                            border: '2px solid',
                                                            borderColor: 'primary.dark',
                                                            bgcolor: 'primary.main'
                                                        }}
                                                    />
                                                    {this.renderServiceStatus(blue, blue.domain === currentServer, blue.deploymentStatus?.status === 'IN_PROGRESS')}
                                                </Box>
                                                <Typography variant="body2" sx={{ mt: 1.5, mb: 2 }}>
                                                    {blue.domain}
                                                </Typography>
                                            </Box>
                                        )}
                                        {!isGetDeployInfo && blue.deploymentStatus ? (
                                            this.renderDeploymentStatusTable(blue.deploymentStatus)
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">
                                                No status information available
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Green Service Status */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{
                                    borderColor: blue.domain !== currentServer ? 'success.main' : 'divider'
                                }} elevation={4}>
                                    <CardContent>
                                        {isGetDeployInfo ? (
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Skeleton variant="circular" width={12} height={12} />
                                                    <Skeleton variant="text" width={100} height={32} />
                                                </Box>
                                                <Skeleton variant="text" width={200} sx={{ mt: 1.5, mb: 2 }} />
                                                <TableContainer sx={{ border: '1px solid rgba(211,227,253,0.08)', borderRadius: '12px' }}>
                                                    <Table>
                                                        <TableBody>
                                                            {[1, 2, 3, 4, 5, 6, 7].map((index) => (
                                                                <TableRow key={index}>
                                                                    <TableCell><Skeleton variant="text" width={100} /></TableCell>
                                                                    <TableCell><Skeleton variant="text" width={200} /></TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        ) : (
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '50%',
                                                            bgcolor: 'success.main',
                                                            border: '2px solid',
                                                            borderColor: 'success.dark'
                                                        }}
                                                    />
                                                    {this.renderServiceStatus(green, blue.domain !== currentServer, green.deploymentStatus?.status === 'IN_PROGRESS')}
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 2 }}>
                                                    {domains.find(d => d !== blue.domain)}
                                                </Typography>
                                            </Box>
                                        )}
                                        {!isGetDeployInfo && green.deploymentStatus ? (
                                            this.renderDeploymentStatusTable(green.deploymentStatus)
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">
                                                No status information available
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                    <Divider />
                    <Box sx={{ p: 2 }}>

                        <Typography variant="h6" gutterBottom>
                            Deploy Setting
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Typography variant="subtitle2"> Select Target Server:</Typography>
                            <TextField
                                value={targetServer || ""}
                                onChange={this.handleTargetServerChange}
                                variant="outlined"
                                size="small"
                                select
                                SelectProps={{
                                    native: true,
                                }}
                            >
                                <option value="">Select target server</option>
                                {domains.map((domain, index) => (
                                    <option key={index} value={domain}>
                                        {domain} {domain === currentServer ? '(current)' : ''}
                                    </option>
                                ))}
                            </TextField>
                        </Stack>

                        {targetServer === currentServer && (
                            <Alert severity="warning">
                                <AlertTitle>Warning</AlertTitle>
                                You are deploying to the current active server, all connections will be interrupted.
                            </Alert>
                        )}
                    </Box>

                    <Box sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1">
                                Environment Variables
                            </Typography>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={this.handleAddEnvironmentVariable}
                                variant="outlined"
                                size="small"
                            >
                                Add Environment Variable
                            </Button>
                        </Box>
                        {envVariables.map((env, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <TextField
                                    size="small"
                                    label="Key"
                                    value={env.key}
                                    onChange={(e) => this.handleEnvironmentVariableChange(index, 'key', e.target.value)}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    size="small"
                                    label="Value"
                                    value={env.value}
                                    onChange={(e) => this.handleEnvironmentVariableChange(index, 'value', e.target.value)}
                                    sx={{ flex: 1 }}
                                />
                                <IconButton
                                    onClick={() => this.handleRemoveEnvironmentVariable(index)}
                                    color="error"
                                    size="small"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ p: 2 }}>
                        {/* <Typography variant="body2" color="text.secondary">
                            Debug: isLoading = {isLoading.toString()} -- targetServer = {targetServer || 'null'} -- isDeploying = {isDeploying.toString()}
                        </Typography>    */}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={this.handleDeployClick}
                            disabled={isLoading || !targetServer || isDeploying}
                            startIcon={isDeploying ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isDeploying ? 'Deploying...' : 'Deploy'}
                        </Button>
                    </Box>

                </CardContent>

                <Dialog
                    open={isConfirmDialogOpen}
                    onClose={this.handleCancelDeploy}
                >
                    <DialogTitle>Confirm Deployment</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to deploy to {targetServer || 'N/A'}?
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                            Build: {latestImage?.imageTag || 'N/A'}
                        </Typography>
                        {envVariables.length > 0 && (
                            <>
                                <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                                    Environment Variables:
                                </Typography>
                                {envVariables.map((env, index) => (
                                    <Typography key={index} variant="body2" color="textSecondary">
                                        {env.key}: {env.value}
                                    </Typography>
                                ))}
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={this.handleCancelDeploy}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={this.handleConfirmDeploy} 
                            color="primary" 
                            variant="contained"
                            disabled={isDeploying}
                            startIcon={isDeploying ? <CircularProgress size={20} color="inherit" /> : null}
                        >
                            {isDeploying ? 'Deploying...' : 'Deploy'}
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={this.state.activeServer !== null}
                    onClose={() => this.setState({ activeServer: null })}
                >
                    <DialogTitle>Confirm Active Server</DialogTitle>
                    <DialogContent>
                        <Box>
                            <Typography>Are you sure you want to change active server to </Typography>
                            <Typography component="span" fontWeight="bold" color="primary">{this.state.activeServer}</Typography>                          
                            <Typography>New connections will be made to this server while the old one is still running.</Typography>
                        </Box>
                        
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => this.setState({ activeServer: null })}>Cancel</Button>
                        <Button onClick={this.handleConfirmActiveServer}>Confirm</Button>
                    </DialogActions>
                </Dialog>
                
            </Card>
        );
    }
} 